import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Edit3, Loader2, Plus, Save, Trash2, X } from "lucide-react";
import api from "../api/api";
import { useDialog } from "./DialogProvider";
import { PremiumCard, StatusBadge } from "./ui";

function groupBundles(raw) {
  const map = new Map();
  (raw || []).forEach((r) => {
    const id = Number(r?.bundle_id);
    if (!Number.isInteger(id) || id <= 0) return;
    if (!map.has(id)) map.set(id, { id, name: r.bundle_name || `Bundle #${id}` });
  });
  return Array.from(map.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

const emptyForm = { tujuan: "E-Commerce", source: "Shopee", total_qty: 1, bundle_id: "", active: 1 };

function RuleModal({ open, form, setForm, bundles, editingId, busy, onClose, onSubmit }) {
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <div className="text-base font-semibold text-slate-950">
              {editingId ? "Edit Auto Bundle Rule" : "Tambah Auto Bundle Rule"}
            </div>
            <div className="mt-1 text-xs text-slate-500">Atur rule otomatis berdasarkan tujuan, source, dan total qty.</div>
          </div>
          <button type="button" className="btn btn-secondary px-3" onClick={onClose} disabled={busy} aria-label="Tutup">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="overflow-y-auto px-5 py-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5">
              <span className="text-xs font-semibold text-slate-600">Tujuan</span>
              <select className="input" value={form.tujuan} onChange={(e) => setForm((f) => ({ ...f, tujuan: e.target.value }))}>
                <option value="E-Commerce">E-Commerce</option>
                <option value="Manual">Manual</option>
                <option value="Defect">Defect</option>
                <option value="Sampel">Sampel</option>
                <option value="Vendor">Vendor</option>
              </select>
            </label>

            <label className="grid gap-1.5">
              <span className="text-xs font-semibold text-slate-600">Source</span>
              <select className="input" value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}>
                <option value="Shopee">Shopee</option>
                <option value="Tokopedia">Tokopedia</option>
                <option value="Spare">Spare</option>
              </select>
            </label>

            <label className="grid gap-1.5">
              <span className="text-xs font-semibold text-slate-600">Total Qty</span>
              <input
                className="input"
                type="number"
                min="1"
                value={form.total_qty}
                onChange={(e) => setForm((f) => ({ ...f, total_qty: e.target.value }))}
              />
            </label>

            <label className="grid gap-1.5">
              <span className="text-xs font-semibold text-slate-600">Status</span>
              <select className="input" value={form.active} onChange={(e) => setForm((f) => ({ ...f, active: Number(e.target.value) }))}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </label>

            <label className="grid gap-1.5 sm:col-span-2">
              <span className="text-xs font-semibold text-slate-600">Bundle</span>
              <select className="input" value={form.bundle_id} onChange={(e) => setForm((f) => ({ ...f, bundle_id: e.target.value }))}>
                <option value="">Pilih bundle</option>
                {bundles.map((b) => <option key={b.id} value={b.id}>{b.name} (#{b.id})</option>)}
              </select>
            </label>
          </div>

          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" className="btn btn-secondary justify-center" onClick={onClose} disabled={busy}>
              Batal
            </button>
            <button className="btn btn-primary justify-center" disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {editingId ? "Simpan Perubahan" : "Tambah Rule"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default function AutoBundleRulesPanel({ bundlesRaw, loading: parentLoading }) {
  const dialog = useDialog();
  const [rules, setRules] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const bundles = useMemo(() => groupBundles(bundlesRaw), [bundlesRaw]);

  const fetchRules = async () => {
    const res = await api.get("/api/auto-bundle-rules", { noCache: true });
    setRules(Array.isArray(res.data) ? res.data : []);
  };

  useEffect(() => {
    fetchRules().catch(() => setRules([]));
  }, []);

  const closeModal = () => {
    if (busy) return;
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      tujuan: rule.tujuan || "E-Commerce",
      source: rule.source || "Shopee",
      total_qty: rule.total_qty || 1,
      bundle_id: rule.bundle_id || "",
      active: Number(rule.active) ? 1 : 0,
    });
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        total_qty: Number(form.total_qty),
        bundle_id: Number(form.bundle_id),
        active: form.active ? 1 : 0,
      };
      if (editingId) {
        await api.put(`/api/auto-bundle-rules/${editingId}`, payload);
      } else {
        await api.post("/api/auto-bundle-rules", payload);
      }
      await fetchRules();
      setModalOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      dialog.toast({ type: "success", title: "Auto Bundle Rules", message: editingId ? "Rule diupdate." : "Rule dibuat." });
    } catch (err) {
      dialog.toast({ type: "error", title: "Auto Bundle Rules", message: err?.response?.data?.message || err.message || "Gagal simpan rule." });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (rule) => {
    const ok = await dialog.confirm(
      `Hapus auto bundle rule?\n\n${rule.tujuan} / ${rule.source} / Qty ${rule.total_qty}`,
      { title: "Hapus Rule", confirmText: "Hapus", cancelText: "Batal" }
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/api/auto-bundle-rules/${rule.id}`);
      await fetchRules();
    } catch (err) {
      dialog.toast({ type: "error", title: "Auto Bundle Rules", message: err?.response?.data?.message || err.message || "Gagal hapus rule." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PremiumCard className="p-4 lg:p-5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-base font-semibold text-slate-950">Auto Bundle Rules</div>
          <div className="mt-1 text-xs text-slate-500">Pilih bundle otomatis berdasarkan tujuan, source, dan total qty pesanan marketplace.</div>
        </div>
        <button type="button" className="btn btn-primary justify-center" onClick={openCreate} disabled={busy || parentLoading}>
          <Plus className="h-4 w-4" />
          Tambah Rule
        </button>
      </div>

      <div className="overflow-auto rounded-xl border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
            <tr>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Tujuan</th>
              <th className="px-3 py-3">Source</th>
              <th className="px-3 py-3">Total Qty</th>
              <th className="px-3 py-3">Bundle</th>
              <th className="px-3 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rules.length ? rules.map((rule) => (
              <tr key={rule.id}>
                <td className="px-3 py-3"><StatusBadge tone={Number(rule.active) ? "emerald" : "slate"}>{Number(rule.active) ? "ACTIVE" : "INACTIVE"}</StatusBadge></td>
                <td className="px-3 py-3">{rule.tujuan}</td>
                <td className="px-3 py-3">{rule.source}</td>
                <td className="px-3 py-3 tabular-nums">{rule.total_qty}</td>
                <td className="px-3 py-3">{rule.bundle_name || `#${rule.bundle_id}`}</td>
                <td className="px-3 py-3">
                  <div className="flex justify-end gap-2">
                    <button type="button" className="btn btn-secondary px-3" onClick={() => openEdit(rule)} disabled={busy}>
                      <Edit3 className="h-4 w-4" />
                      Edit
                    </button>
                    <button type="button" className="btn btn-secondary px-3" onClick={() => remove(rule)} disabled={busy} aria-label="Hapus rule">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td className="px-3 py-4 text-slate-500" colSpan={6}>Belum ada auto bundle rule.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <RuleModal
        open={modalOpen}
        form={form}
        setForm={setForm}
        bundles={bundles}
        editingId={editingId}
        busy={busy}
        onClose={closeModal}
        onSubmit={submit}
      />
    </PremiumCard>
  );
}
